/**
 * TelemetryService (Firebase Analytics + Performance + Crash bridge)
 *
 * Field-tech first, progressive enhancement, network-aware, config-driven.
 * - Never blocks app start or critical flows.
 * - All calls are safe no-ops when disabled or uninitialized.
 * - Conservative defaults for employee/subcontractor on cellular/metered.
 * - Remote Config for kill switches + sampling (fetched once, long cache).
 */

import type { User, UserRole } from "./api";

// Dynamic imports to avoid bloating initial bundle and to keep graceful when firebase not configured.
let app: any = null;
let analytics: any = null;
let perf: any = null;
let remoteConfig: any = null;

let initialized = false;
let initPromise: Promise<void> | null = null;

export interface NetworkState {
  type: "wifi" | "cellular" | "ethernet" | "unknown" | "none";
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  downlink?: number;
  saveData?: boolean;
  isMetered: boolean;
}

export interface TelemetryDecision {
  crashEnabled: boolean;
  perfEnabled: boolean;
  perfSampleRate: number;
  analyticsEnabled: boolean;
  analyticsSampleRate: number;
}

let currentNetwork: NetworkState = {
  type: "unknown",
  isMetered: false,
};

let currentUserRole: UserRole | undefined;
let decision: TelemetryDecision = {
  crashEnabled: true,
  perfEnabled: false,
  perfSampleRate: 0.05,
  analyticsEnabled: false,
  analyticsSampleRate: 0.1,
};

let remoteDefaultsApplied = false;

// Captured fns from dynamic imports (to avoid scope issues)
let setUserPropertiesFn: ((analytics: any, props: Record<string, string>) => void) | null = null;
let setAnalyticsCollectionEnabledFn: ((analytics: any, enabled: boolean) => void) | null = null;

// ------------------------------
// Network monitor (best effort, no polyfill bloat)
// ------------------------------
function getNavigatorConnection(): any {
  if (typeof navigator === "undefined") return null;
  return (
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection ||
    null
  );
}

function computeNetworkState(conn: any): NetworkState {
  if (!conn) {
    return { type: navigator?.onLine === false ? "none" : "unknown", isMetered: false };
  }
  const t = (conn.type || "").toLowerCase();
  let type: NetworkState["type"] = "unknown";
  if (t === "wifi" || t === "ethernet" || t === "cellular") type = t as any;
  else if (t === "none") type = "none";

  const eff = conn.effectiveType as NetworkState["effectiveType"] | undefined;
  const downlink = typeof conn.downlink === "number" ? conn.downlink : undefined;
  const saveData = !!conn.saveData;

  const isMetered = type === "cellular" || saveData;

  return { type, effectiveType: eff, downlink, saveData, isMetered };
}

function updateNetworkFromConnection(): void {
  const conn = getNavigatorConnection();
  currentNetwork = computeNetworkState(conn);
  // Recompute decision (role may be known)
  recomputeDecision();
  // Push as user property when possible (non blocking)
  if (analytics && currentNetwork.type !== "unknown") {
    try {
      // setUserProperties is safe post-init
      setUserPropertySafe("network_type", currentNetwork.type);
      if (currentNetwork.isMetered) setUserPropertySafe("network_metered", "true");
    } catch {}
  }
}

let networkListenerAttached = false;
function attachNetworkListener(): void {
  if (networkListenerAttached) return;
  const conn = getNavigatorConnection();
  if (conn && typeof conn.addEventListener === "function") {
    conn.addEventListener("change", () => {
      updateNetworkFromConnection();
    });
  }
  // Also react to basic online/offline as fallback
  window.addEventListener("online", updateNetworkFromConnection);
  window.addEventListener("offline", updateNetworkFromConnection);
  networkListenerAttached = true;
}

export function getNetworkState(): NetworkState {
  return currentNetwork;
}

// ------------------------------
// Decision engine (pure + remote overrides)
// ------------------------------
function isFieldTechRole(role?: UserRole): boolean {
  return role === "employee" || role === "subcontractor";
}

function isPoorNetwork(n: NetworkState): boolean {
  if (n.type === "cellular" || n.isMetered) return true;
  if (n.effectiveType === "slow-2g" || n.effectiveType === "2g") return true;
  return false;
}

function recomputeDecision(): void {
  const isDev = import.meta.env?.DEV === true || import.meta.env?.MODE === "development";
  const field = isFieldTechRole(currentUserRole);
  const poor = isPoorNetwork(currentNetwork);

  // Base conservative policy
  let crashEnabled = true;
  let perfEnabled = !(field && poor);
  let perfSample = field ? 0.05 : isDev ? 1.0 : 0.15;
  let analyticsEnabled = !(field && poor);
  let analyticsSample = field ? 0.08 : isDev ? 1.0 : 0.4;

  // Remote Config overrides (if loaded)
  if (remoteConfig) {
    try {
      const kill = getRemoteValueBool("telemetry_kill_switch", false);
      if (kill) {
        crashEnabled = false;
        perfEnabled = false;
        analyticsEnabled = false;
      } else {
        const rcCrash = getRemoteValueBool("crash_enabled", true);
        crashEnabled = rcCrash;

        const rcPerf = getRemoteValueBool("perf_enabled", true);
        perfEnabled = perfEnabled && rcPerf;

        const rcPerfSample = getRemoteValueNumber("perf_sample_rate", perfSample);
        perfSample = Math.max(0, Math.min(1, rcPerfSample));

        const rcAna = getRemoteValueBool("analytics_enabled", true);
        analyticsEnabled = analyticsEnabled && rcAna;

        const rcAnaSample = getRemoteValueNumber("analytics_sample_rate", analyticsSample);
        analyticsSample = Math.max(0, Math.min(1, rcAnaSample));
      }
    } catch {
      // ignore remote read errors — keep local conservative values
    }
  }

  decision = {
    crashEnabled,
    perfEnabled,
    perfSampleRate: perfSample,
    analyticsEnabled,
    analyticsSampleRate: analyticsSample,
  };
}

function getRemoteValueBool(key: string, fallback: boolean): boolean {
  if (!remoteConfig) return fallback;
  try {
    const val = remoteConfig.getValue ? remoteConfig.getValue(key) : null;
    if (!val) return fallback;
    const s = (val.asString ? val.asString() : String(val)).toLowerCase().trim();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function getRemoteValueNumber(key: string, fallback: number): number {
  if (!remoteConfig) return fallback;
  try {
    const val = remoteConfig.getValue ? remoteConfig.getValue(key) : null;
    if (!val) return fallback;
    const n = val.asNumber ? val.asNumber() : parseFloat(val.asString ? val.asString() : String(val));
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

export function getDecision(): TelemetryDecision {
  return { ...decision };
}

export function setUserRole(role: UserRole): void {
  currentUserRole = role;
  recomputeDecision();
  setUserPropertySafe("user_role", role);
  setUserPropertySafe("is_field_tech", isFieldTechRole(role) ? "true" : "false");
}

// ------------------------------
// Firebase initialization (lazy + async + safe)
// ------------------------------
function getFirebaseConfig() {
  const apiKey = import.meta.env?.VITE_FIREBASE_API_KEY;
  const authDomain = import.meta.env?.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = import.meta.env?.VITE_FIREBASE_PROJECT_ID;
  const storageBucket = import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET;
  const messagingSenderId = import.meta.env?.VITE_FIREBASE_MESSAGING_SENDER_ID;
  const appId = import.meta.env?.VITE_FIREBASE_APP_ID;
  const measurementId = import.meta.env?.VITE_FIREBASE_MEASUREMENT_ID;

  if (!apiKey || !projectId || !appId) {
    // Not configured — graceful no-op mode
    return null;
  }
  const config: Record<string, string> = {
    apiKey,
    projectId,
    appId,
  };
  if (authDomain) config.authDomain = authDomain;
  if (storageBucket) config.storageBucket = storageBucket;
  if (messagingSenderId) config.messagingSenderId = messagingSenderId;
  // NOTE: We intentionally do not include measurementId here to avoid
  // the "measurement ID does not match the one fetched from the server" warning
  // when the Firebase project does not have a matching Analytics web stream configured.
  // If you need a specific measurement ID, set it up in Firebase and include it.
  // if (measurementId) config.measurementId = measurementId;
  return config;
}

async function doInitialize(): Promise<void> {
  if (initialized) return;

  const cfg = getFirebaseConfig();
  if (!cfg) {
    // No config provided (dev or not yet provisioned). Telemetry stays off.
    initialized = true;
    return;
  }

  try {
    const appMod = await import("firebase/app");
    const initializeApp = appMod.initializeApp || appMod.default?.initializeApp;
    if (initializeApp) {
      app = initializeApp(cfg as any);
    } else {
      initialized = true;
      return;
    }

    // Always attempt analytics (used for events + error-as-event + future Crashlytics bridge)
    const analyticsMod = await import("firebase/analytics");
    const getAnalytics = (analyticsMod as any).getAnalytics || (analyticsMod as any).default?.getAnalytics;
    const setAnalyticsCollectionEnabled = (analyticsMod as any).setAnalyticsCollectionEnabled || (analyticsMod as any).default?.setAnalyticsCollectionEnabled;
    const setUserPropertiesFromMod = (analyticsMod as any).setUserProperties || (analyticsMod as any).default?.setUserProperties;

    analytics = (getAnalytics ? getAnalytics(app) : null) || null;

    setAnalyticsCollectionEnabledFn = setAnalyticsCollectionEnabled || null;
    setUserPropertiesFn = setUserPropertiesFromMod || null;

    // Performance: only init if we may use it. We primarily gate at the call sites (startTrace).
    // Avoid calling non-existent set* methods for web SDK compatibility.
    if (decision.perfEnabled) {
      const perfMod = await import("firebase/performance");
      const getPerformance = (perfMod as any).getPerformance || (perfMod as any).default?.getPerformance;
      perf = getPerformance(app);
    }

    // Remote Config (lightweight fetch, long cache)
    const rcMod = await import("firebase/remote-config");
    const getRemoteConfig = (rcMod as any).getRemoteConfig || (rcMod as any).default?.getRemoteConfig;
    const fetchAndActivate = (rcMod as any).fetchAndActivate || (rcMod as any).default?.fetchAndActivate;
    const setDefaults = (rcMod as any).setDefaults || (rcMod as any).default?.setDefaults;

    if (getRemoteConfig) {
      remoteConfig = getRemoteConfig(app);
    }
    // Conservative defaults (overridden by server values after fetch)
    if (setDefaults && remoteConfig) {
      setDefaults(remoteConfig, {
        telemetry_kill_switch: false,
        crash_enabled: true,
        perf_enabled: true,
        perf_sample_rate: 0.05,
        analytics_enabled: true,
        analytics_sample_rate: 0.1,
      });
    }

    // Fetch non-blocking; do not await for critical path
    // Default minimum is 12h in prod; we rely on that + explicit fetch on online
    if (fetchAndActivate && remoteConfig) {
      fetchAndActivate(remoteConfig)
        .then(() => {
          remoteDefaultsApplied = true;
          recomputeDecision();
          // Re-apply collection toggle after remote (if fn available)
          try {
            if (analytics && setAnalyticsCollectionEnabledFn) {
              setAnalyticsCollectionEnabledFn(analytics, decision.analyticsEnabled);
            }
          } catch {}
        })
        .catch(() => {
          // keep local decision
        });
    }

    // Initial properties (role may be set later)
    setUserPropertySafe("platform", "web_pwa");

    // Analytics collection gate (default on; we gate calls + can disable)
    try {
      if (analytics && setAnalyticsCollectionEnabledFn) {
        setAnalyticsCollectionEnabledFn(analytics, decision.analyticsEnabled);
      }
    } catch {}

    initialized = true;

    // React to network changes going forward
    attachNetworkListener();
    updateNetworkFromConnection();
  } catch (e) {
    // Any failure: telemetry is best-effort. Core app unaffected.
    // eslint-disable-next-line no-console
    if (import.meta.env?.DEV) console.debug("[telemetry] init failed (safe no-op)", e);
    initialized = true;
  }
}

export async function initializeTelemetry(user?: User): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = doInitialize().then(() => {
    if (user) {
      setUserRole(user.role);
      // Also set a stable id-ish (avoid PII)
      setUserPropertySafe("user_id_hash", simpleHash(user.id.toString()));
    }
    return;
  });
  return initPromise;
}

function simpleHash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return ("0000000" + h.toString(16)).slice(-8);
}

function setUserPropertySafe(key: string, value: string): void {
  if (!analytics) return;
  try {
    if (setUserPropertiesFn) {
      setUserPropertiesFn(analytics, { [key]: value });
    } else {
      // Fallback dynamic import if not captured yet
      import("firebase/analytics").then((mod) => {
        const fn = mod.setUserProperties || mod.default?.setUserProperties;
        if (fn) fn(analytics, { [key]: value });
      }).catch(() => {});
    }
  } catch {}
}

// ------------------------------
// Public safe APIs (always callable)
// ------------------------------
export function logEvent(name: string, params?: Record<string, unknown>): void {
  if (!analytics || !decision.analyticsEnabled) return;
  if (Math.random() > decision.analyticsSampleRate) return;
  try {
    // Clean params — strip anything that looks like PII or large
    const clean: Record<string, any> = {};
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (typeof v === "string" && v.length > 100) continue;
        if (k.toLowerCase().includes("gps") || k.toLowerCase().includes("lat") || k.toLowerCase().includes("lon")) continue;
        if (k.toLowerCase().includes("selfie") || k.toLowerCase().includes("photo")) continue;
        clean[k] = v;
      }
    }
    // firebase logEvent
    import("firebase/analytics").then((mod) => {
      const fbLog = mod.logEvent || mod.default?.logEvent;
      if (fbLog) fbLog(analytics, name, clean);
    }).catch(() => {});
  } catch {}
}

export interface TraceHandle {
  stop: (extra?: Record<string, string | number>) => void;
}

/** Firebase Performance attribute values max out ~100 chars; long Tailwind class names throw. */
const PERF_ATTR_MAX = 80;

function safePerfAttribute(value: string | number): string {
  const s = String(value);
  return s.length > PERF_ATTR_MAX ? s.slice(0, PERF_ATTR_MAX) : s;
}

export function startTrace(name: string): TraceHandle {
  if (!perf || !decision.perfEnabled) {
    return { stop: () => {} };
  }
  if (Math.random() > decision.perfSampleRate) {
    return { stop: () => {} };
  }
  let stopped = false;
  let traceObj: any = null;

  // Use dynamic to ensure module is there
  void import("firebase/performance").then((mod) => {
    const createTrace = mod.trace || mod.default?.trace;
    if (stopped || !perf || !createTrace) return;
    try {
      traceObj = createTrace(perf, name);
      if (traceObj && typeof traceObj.start === "function") traceObj.start();
      // seed some attrs
      if (traceObj?.putAttribute) {
        try {
          traceObj.putAttribute("network_type", safePerfAttribute(currentNetwork.type));
          if (currentUserRole) traceObj.putAttribute("user_role", safePerfAttribute(currentUserRole));
        } catch {
          // Never let telemetry attribute limits break the app
        }
      }
    } catch {}
  });

  return {
    stop: (extra?: Record<string, string | number>) => {
      stopped = true;
      if (!traceObj) return;
      try {
        if (extra && traceObj.putAttribute) {
          for (const [k, v] of Object.entries(extra)) {
            try {
              // Keys must be short too; skip className-like junk entirely.
              if (k.length > 40 || /class|className|css/i.test(k)) continue;
              traceObj.putAttribute(k, safePerfAttribute(v));
            } catch {
              // ignore single attribute failures
            }
          }
        }
        if (typeof traceObj.stop === "function") traceObj.stop();
      } catch {}
    },
  };
}

export function recordError(error: Error | string): void {
  if (!decision.crashEnabled) return;
  const message = typeof error === "string" ? error : error.message;
  const stack = typeof error === "string" ? undefined : error.stack;

  // Always try to log via analytics as non-fatal (works today; future Crashlytics web will map)
  if (analytics) {
    try {
      import("firebase/analytics").then((mod) => {
        const fbLog = mod.logEvent || mod.default?.logEvent;
        if (fbLog) {
          fbLog(analytics, "app_exception", {
            message: message.slice(0, 300),
            has_stack: !!stack,
            fatal: false,
          });
        }
      }).catch(() => {});
    } catch {}
  }

  // Also push a breadcrumb-style log when possible (perf or analytics)
  if (perf) {
    try {
      // performance has no direct breadcrumb, but we can ignore or use log if exposed later
    } catch {}
  }
}

export function updateNetworkState(): void {
  updateNetworkFromConnection();
}

// Optional helper for tests / dev
export function _resetForTests() {
  initialized = false;
  initPromise = null;
  app = null;
  analytics = null;
  perf = null;
  remoteConfig = null;
  decision = { crashEnabled: true, perfEnabled: false, perfSampleRate: 0.05, analyticsEnabled: false, analyticsSampleRate: 0.1 };
}
